/**
 * Minimal RFC5545 (.ics) parser for the KBT-176 "events from the shared
 * Google Calendar" feature.
 *
 * We only implement what a Google Calendar public/secret ICS export
 * actually needs on the read side: VEVENT blocks with SUMMARY,
 * DESCRIPTION, LOCATION, DTSTART/DTEND (date or date-time), a single
 * RRULE, and any number of EXDATE lines. Anything else in the feed
 * (VTIMEZONE, VALARM, other calendar components, etc.) is ignored.
 *
 * Known simplification: DTSTART/DTEND values that carry a TZID parameter
 * (as Google's feed does for most timed events, e.g.
 * "DTSTART;TZID=America/Los_Angeles:20260101T100000") are treated as a
 * naive wall-clock time in the *browser's* local timezone rather than
 * being converted from the named IANA zone. UTC values (trailing "Z")
 * and all-day VALUE=DATE values are handled exactly. Getting true
 * per-event timezone conversion right would require bundling a full tz
 * database; given KBT is a single-location shul where the calendar owner
 * and the visitors are in the same timezone, this trade-off is
 * intentional and documented rather than silently wrong. Revisit if KBT
 * ever schedules events explicitly in a different timezone than the
 * viewer.
 */
import { RRule } from 'rrule';

// ----------------------------------------------------------------------
// Low-level line handling
// ----------------------------------------------------------------------

// RFC5545 line folding: a long line is wrapped across multiple physical
// lines, and every continuation line starts with a single space or tab
// that must be removed when unfolding.
function unfoldLines(icsText) {
  const rawLines = icsText.replace(/\r\n/g, '\n').split('\n');
  const lines = [];
  rawLines.forEach((line) => {
    if ((line.startsWith(' ') || line.startsWith('\t')) && lines.length > 0) {
      lines[lines.length - 1] += line.slice(1);
    } else {
      lines.push(line);
    }
  });
  return lines.filter((line) => line.length > 0);
}

// Splits "NAME;PARAM=VAL;PARAM2=VAL2:VALUE" into { name, params, value }.
// Google's feed never puts a colon inside a param value for the
// properties we care about, so splitting on the first colon is safe.
function parseLine(line) {
  const colonIndex = line.indexOf(':');
  if (colonIndex === -1) return null;
  const head = line.slice(0, colonIndex);
  const value = line.slice(colonIndex + 1);
  const [name, ...paramParts] = head.split(';');
  const params = {};
  paramParts.forEach((part) => {
    const eq = part.indexOf('=');
    if (eq !== -1) params[part.slice(0, eq).toUpperCase()] = part.slice(eq + 1);
  });
  return { name: name.toUpperCase(), params, value };
}

// Unescapes RFC5545 TEXT values (SUMMARY/DESCRIPTION/LOCATION).
function unescapeText(value) {
  return value
    .replace(/\\n/gi, '\n')
    .replace(/\\,/g, ',')
    .replace(/\\;/g, ';')
    .replace(/\\\\/g, '\\');
}

// Parses a DTSTART/DTEND/EXDATE value into a JS Date, honoring
// VALUE=DATE (all-day) and a trailing "Z" (UTC). See the timezone caveat
// in the module comment above.
function parseDateValue(value, params) {
  const isAllDay = params.VALUE === 'DATE' || /^\d{8}$/.test(value);

  if (isAllDay) {
    const year = Number(value.slice(0, 4));
    const month = Number(value.slice(4, 6)) - 1;
    const day = Number(value.slice(6, 8));
    return { date: new Date(year, month, day), allDay: true };
  }

  const match = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})(Z)?$/.exec(value);
  if (!match) return { date: null, allDay: false };

  const [, y, mo, d, h, mi, s, isUtc] = match;
  const parts = [Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s)];
  const date = isUtc ? new Date(Date.UTC(...parts)) : new Date(...parts);
  return { date, allDay: false };
}

// ----------------------------------------------------------------------
// Public API
// ----------------------------------------------------------------------

/**
 * Parses raw ICS text into an array of event descriptors, one per
 * VEVENT block. Recurring events are returned once, still carrying their
 * raw RRULE string — call getUpcomingEvents to expand them.
 */
export function parseICalEvents(icsText) {
  if (!icsText || typeof icsText !== 'string') return [];

  const lines = unfoldLines(icsText);
  const events = [];
  let current = null;

  lines.forEach((rawLine) => {
    const line = rawLine.trim();
    if (line === 'BEGIN:VEVENT') {
      current = {
        uid: null,
        title: '',
        description: '',
        location: '',
        start: null,
        end: null,
        allDay: false,
        rrule: null,
        exdates: [],
      };
      return;
    }
    if (line === 'END:VEVENT') {
      if (current && current.start) events.push(current);
      current = null;
      return;
    }
    if (!current) return;

    const parsed = parseLine(line);
    if (!parsed) return;
    const { name, params, value } = parsed;

    switch (name) {
      case 'UID':
        current.uid = value;
        break;
      case 'SUMMARY':
        current.title = unescapeText(value);
        break;
      case 'DESCRIPTION':
        current.description = unescapeText(value);
        break;
      case 'LOCATION':
        current.location = unescapeText(value);
        break;
      case 'DTSTART': {
        const { date, allDay } = parseDateValue(value, params);
        current.start = date;
        current.allDay = allDay;
        break;
      }
      case 'DTEND': {
        const { date } = parseDateValue(value, params);
        current.end = date;
        break;
      }
      case 'RRULE':
        current.rrule = value;
        break;
      case 'EXDATE': {
        const { date } = parseDateValue(value, params);
        if (date) current.exdates.push(date);
        break;
      }
      default:
        break;
    }
  });

  // Fall back to a synthetic UID if the feed ever omits one, so React
  // keys / occurrence ids downstream stay stable within a single parse.
  events.forEach((event, index) => {
    if (!event.uid) event.uid = `event-${index}`;
  });

  return events;
}

// Expands a single recurring event into concrete occurrences between
// `from` and `to`, skipping any occurrence whose start matches an EXDATE.
function expandRecurringEvent(event, from, to) {
  let rule;
  try {
    rule = new RRule({ ...RRule.parseString(event.rrule), dtstart: event.start });
  } catch (err) {
    // A malformed RRULE shouldn't take down the whole feed — treat the
    // event as non-recurring instead.
    return [{ ...event, isRecurring: false, id: event.uid }];
  }

  const durationMs = event.end ? event.end.getTime() - event.start.getTime() : 0;
  const exdateTimes = new Set(event.exdates.map((d) => d.getTime()));

  return rule
    .between(from, to, true)
    .filter((occurrenceStart) => !exdateTimes.has(occurrenceStart.getTime()))
    .map((occurrenceStart) => ({
      ...event,
      start: occurrenceStart,
      end: new Date(occurrenceStart.getTime() + durationMs),
      isRecurring: true,
      id: `${event.uid}::${occurrenceStart.toISOString()}`,
    }));
}

/**
 * Parses raw ICS text and returns the upcoming events (non-recurring
 * events that haven't ended yet, plus every future occurrence of
 * recurring ones), sorted by start date ascending.
 *
 * @param {string} icsText
 * @param {{ now?: Date, horizonDays?: number }} [options]
 *   now - treated as "the present" for filtering past events (defaults
 *         to `new Date()`; tests pass a fixed value for determinism).
 *   horizonDays - how far into the future to expand recurring events
 *         (defaults to 365 days) so an indefinitely-recurring RRULE
 *         doesn't generate an unbounded list.
 */
export function getUpcomingEvents(icsText, { now = new Date(), horizonDays = 365 } = {}) {
  const events = parseICalEvents(icsText);
  const horizonEnd = new Date(now.getTime() + horizonDays * 24 * 60 * 60 * 1000);

  const occurrences = events.flatMap((event) => {
    if (event.rrule) return expandRecurringEvent(event, now, horizonEnd);
    return [{ ...event, isRecurring: false, id: event.uid }];
  });

  return occurrences
    .filter((occurrence) => (occurrence.end ?? occurrence.start).getTime() >= now.getTime())
    .sort((a, b) => a.start.getTime() - b.start.getTime());
}
